import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'cm_cms_cache_v1';
const CmsContext = createContext(null);

const darkenHex = (hex, amount) => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const num = parseInt(full, 16);
  const r = Math.max(0, Math.floor(((num >> 16) & 255) * (1 - amount)));
  const g = Math.max(0, Math.floor(((num >> 8) & 255) * (1 - amount)));
  const b = Math.max(0, Math.floor((num & 255) * (1 - amount)));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const applyPrimary = (hex) => {
  if (!hex) return;
  const root = document.documentElement;
  root.style.setProperty('--cm-primary', hex);
  root.style.setProperty('--cm-primary-dark', darkenHex(hex, 0.18));
};

// Read cached CMS on first render, synchronously, so the colour + texts + images
// are correct from the very first paint and we avoid a "flash" of the default theme.
const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && data.primary_color) return data;
  } catch {
    /* corrupt cache — ignore and fall through */
  }
  return null;
};

const initial = readCache();
// Apply cached primary color *before* React renders anything so even the very first
// frame uses the right brand colour.
if (initial?.primary_color) {
  applyPrimary(initial.primary_color);
}

export const CmsProvider = ({ children }) => {
  const [primaryColor, setPrimaryColor] = useState(initial?.primary_color || '#13498f');
  const [texts, setTexts] = useState(initial?.texts || {});
  const [images, setImages] = useState(initial?.images || {});
  const [team, setTeam] = useState(initial?.team || []);
  const [cocktailCategories, setCocktailCategories] = useState(initial?.cocktail_categories || []);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cms`);
      setPrimaryColor(data.primary_color);
      setTexts(data.texts || {});
      setImages(data.images || {});
      setTeam(data.team || []);
      setCocktailCategories(data.cocktail_categories || []);
      applyPrimary(data.primary_color);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      } catch {
        /* localStorage might be full or blocked — non-fatal */
      }
    } catch {
      // CMS load errors fall back to defaults; no console noise in production
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    applyPrimary(primaryColor);
  }, [primaryColor]);

  const t = (key, fallback = '') => texts[key] ?? fallback;
  const img = (key, fallback = '') => images[key] || fallback;

  const value = { primaryColor, setPrimaryColor, texts, setTexts, images, setImages, team, setTeam, cocktailCategories, setCocktailCategories, t, img, refresh, loaded };
  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
};

export const useCms = () => {
  const ctx = useContext(CmsContext);
  if (!ctx) throw new Error('useCms must be used within CmsProvider');
  return ctx;
};
